import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';

export const MAX_CAPTURE_RETRY_ATTEMPTS = 3;

const DEFAULT_MESSAGE =
  "The customer's card was declined when we tried to finalize the payment. The order has not been accepted.";

interface Props {
  message?: string | null;
  attempts: number;
  retrying: boolean;
  onRetry: () => void;
  onReject: () => void;
}

/**
 * Inline error panel rendered on a pending order card when POST /accept returns
 * 409 `payment_capture_failed`. The owner must decide to retry or reject.
 * Sweeper continues to run in the background; after enough failures the
 * server emits an owner-alert event (rendered separately).
 */
export function PaymentCaptureFailedPanel({
  message,
  attempts,
  retrying,
  onRetry,
  onReject,
}: Props) {
  const retriesExhausted = attempts >= MAX_CAPTURE_RETRY_ATTEMPTS;
  const body = message && message.trim().length > 0 ? message : DEFAULT_MESSAGE;

  return (
    <div
      role="alert"
      className="mt-3 border border-destructive/30 bg-destructive/5 rounded-lg p-3 space-y-2"
    >
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-destructive">
            Payment hold could not be completed
          </p>
          <p className="text-xs text-muted-foreground mt-1">{body}</p>
          {attempts > 0 && (
            <p className="text-[11px] text-muted-foreground/80 mt-1">
              Attempt {Math.min(attempts, MAX_CAPTURE_RETRY_ATTEMPTS)} of {MAX_CAPTURE_RETRY_ATTEMPTS}
            </p>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={onRetry}
          disabled={retrying || retriesExhausted}
          className="flex-1"
        >
          {retrying && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {retriesExhausted ? 'Retries exhausted' : 'Try again'}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onReject}
          disabled={retrying}
          className="flex-1"
        >
          Reject order
        </Button>
      </div>
    </div>
  );
}
